<template>
  <div class="club-detail">
    <!-- 載入器 -->
    <div v-if="isLoading" class="flex justify-center items-center h-48">
      <div class="w-12 h-12 rounded-full yin-yang animate-pulse-soft"></div>
    </div>

    <!-- 武場詳情內容 -->
    <div v-else-if="club" class="space-y-6">
      <!-- 武場資訊卡片 -->
      <div class="bg-white/90 backdrop-blur-sm rounded-2xl overflow-hidden post-shadow">
        <div class="md:flex">
          <!-- 武場圖片 -->
          <div class="md:w-1/3">
            <div class="h-64 md:h-full relative overflow-hidden">
              <img 
                :src="club.attachment || 'https://upload.wikimedia.org/wikipedia/commons/thumb/c/c6/Yang_cheng_fu_single_whip_application_2_75.jpg/280px-Yang_cheng_fu_single_whip_application_2_75.jpg'"
                :alt="club.name"
                class="w-full h-full object-cover"
              />
              <div class="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
            </div>
          </div>

          <!-- 武場資訊 -->
          <div class="md:w-2/3 p-6">
            <div class="flex flex-col h-full">
              <!-- 標題和權限 -->
              <div class="mb-4">
                <h1 class="text-3xl font-bold text-wood-800 mb-2">{{ club.name }}</h1>
                <div class="flex items-center space-x-4 text-sm">
                  <span class="text-wood-600">@{{ club.uid || club.id }}</span>
                  <span class="px-3 py-1 bg-wood-200 text-wood-700 rounded-full">{{ club.permission }}</span>
                </div>
              </div>

              <!-- 地址 -->
              <div class="mb-4">
                <h3 class="text-sm font-semibold text-wood-700 mb-1">地址</h3>
                <p class="text-wood-800 flex items-center">
                  <i class="fas fa-map-marker-alt mr-2 text-wood-500"></i>
                  {{ club.position }}
                </p>
              </div>

              <!-- 武場介紹 -->
              <div class="mb-6 flex-1">
                <h3 class="text-sm font-semibold text-wood-700 mb-2">武場介紹</h3>
                <p class="text-wood-800 leading-relaxed">{{ club.note }}</p>
              </div>

              <!-- 操作按鈕 -->
              <div class="flex flex-wrap gap-3">
                <!-- 管理員編輯按鈕 -->
                <button
                  v-if="userStore.isLoggedIn && isAdmin"
                  @click="editClub"
                  class="px-4 py-2 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white rounded-xl transition-all duration-300 transform hover:scale-105 shadow-lg"
                >
                  <i class="fas fa-edit mr-2"></i>
                  編輯武場
                </button>

                <!-- 加入/退出按鈕 -->
                <template v-if="userStore.isLoggedIn">
                  <button
                    v-if="isMember"
                    @click="leaveClub"
                    class="px-4 py-2 bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white rounded-xl transition-all duration-300 transform hover:scale-105 shadow-lg"
                  >
                    <i class="fas fa-sign-out-alt mr-2"></i>
                    退出武場
                  </button>
                  <button
                    v-else
                    @click="joinClub"
                    class="px-4 py-2 bg-gradient-to-r from-wood-500 to-wood-600 hover:from-wood-600 hover:to-wood-700 text-white rounded-xl transition-all duration-300 transform hover:scale-105 shadow-lg"
                  >
                    <i class="fas fa-user-plus mr-2"></i>
                    申請加入
                  </button>
                </template>
                <template v-else>
                  <button 
                    class="px-4 py-2 bg-wood-300 text-wood-800 rounded-xl cursor-not-allowed"
                    disabled
                  >
                    <i class="fas fa-lock mr-2"></i>
                    登入才可加入武場
                  </button>
                </template>

                <!-- 返回按鈕 -->
                <button
                  @click="goBack"
                  class="px-4 py-2 bg-wood-200 hover:bg-wood-300 text-wood-800 rounded-xl transition-all duration-300"
                >
                  <i class="fas fa-arrow-left mr-2"></i>
                  回到武場列表
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 成員管理區域（僅管理員可見） -->
      <div v-if="userStore.isLoggedIn && isAdmin && members.length" class="bg-white/90 backdrop-blur-sm rounded-2xl p-6 post-shadow">
        <h2 class="text-xl font-bold text-wood-800 mb-4">
          <i class="fas fa-users mr-2"></i>
          武場成員管理
        </h2>
        
        <div class="overflow-x-auto">
          <table class="w-full">
            <thead>
              <tr class="border-b border-wood-200">
                <th class="text-left py-3 px-2 text-wood-700 font-semibold">帳號</th>
                <th class="text-left py-3 px-2 text-wood-700 font-semibold">筆名</th>
                <th class="text-left py-3 px-2 text-wood-700 font-semibold">自介</th>
                <th class="text-left py-3 px-2 text-wood-700 font-semibold">加入時間</th>
                <th class="text-left py-3 px-2 text-wood-700 font-semibold">狀態</th>
                <th class="text-left py-3 px-2 text-wood-700 font-semibold">操作</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="member in members" :key="member.id" class="border-b border-wood-100 hover:bg-wood-50">
                <td class="py-3 px-2 text-wood-800">{{ member.user?.uid || member.user?.id }}</td>
                <td class="py-3 px-2 text-wood-800">{{ member.user?.nickname }}</td>
                <td class="py-3 px-2 text-wood-600">{{ member.note || '-' }}</td>
                <td class="py-3 px-2 text-wood-600">{{ formatDate(member.created_at) }}</td>
                <td class="py-3 px-2">
                  <span class="px-2 py-1 text-xs rounded-full" :class="getMemberStatusClass(member.permission)">
                    {{ member.permission }}
                  </span>
                </td>
                <td class="py-3 px-2 space-x-2">
                  <button
                    v-if="member.permission === '等候加入'"
                    @click="approveMember(member)"
                    class="px-3 py-1 bg-green-500 hover:bg-green-600 text-white text-xs rounded-lg transition-colors duration-200"
                  >
                    同意
                  </button>
                  <button
                    v-if="member.permission !== '最高管理員'"
                    @click="kickMember(member)"
                    class="px-3 py-1 bg-red-500 hover:bg-red-600 text-white text-xs rounded-lg transition-colors duration-200"
                  >
                    踢出
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- 武場貼文區域 -->
      <div class="bg-white/90 backdrop-blur-sm rounded-2xl p-6 post-shadow">
        <h2 class="text-xl font-bold text-wood-800 mb-4">
          <i class="fas fa-comments mr-2"></i>
          武場貼文
        </h2>

        <!-- 貼文列表 -->
        <template v-if="posts.length">
          <div class="space-y-4">
            <Post 
              v-for="post in posts"
              :key="post.id"
              :post="post"
              :isLoggedIn="userStore.isLoggedIn"
              :showDetailOnClick="true"
              @like="handleLike"
              @bookmark="handleBookmark"
              @showMore="handleShowMore"
              @showDetail="showPostDetail"
              @edit="openPostForm"
            />
          </div>
          
          <!-- 載入更多按鈕 -->
          <div v-if="hasMorePosts" class="text-center mt-6">
            <button 
              @click="loadMorePosts"
              :disabled="isLoadingPosts"
              class="px-6 py-3 bg-gradient-to-r from-wood-500 to-wood-600 hover:from-wood-600 hover:to-wood-700 text-white rounded-xl transition-all duration-300 transform hover:scale-105 shadow-lg disabled:opacity-50"
            >
              <i v-if="isLoadingPosts" class="fas fa-spinner animate-spin mr-2"></i>
              <i v-else class="fas fa-plus mr-2"></i>
              {{ isLoadingPosts ? '載入中...' : '載入更多貼文' }}
            </button>
          </div>
        </template>

        <!-- 空狀態 -->
        <template v-else>
          <div class="text-center py-12">
            <div class="w-16 h-16 mx-auto mb-4 rounded-full yin-yang opacity-50"></div>
            <h3 class="text-lg font-medium text-wood-700 mb-2">目前沒有貼文</h3>
            <p class="text-wood-500">成為第一個在此武場發表貼文的人！</p>
            <button
              v-if="userStore.isLoggedIn && isMember"
              @click="$emit('openPostForm')"
              class="mt-4 px-6 py-2 bg-gradient-to-r from-wood-500 to-wood-600 hover:from-wood-600 hover:to-wood-700 text-white rounded-xl transition-all duration-300 transform hover:scale-105 shadow-lg"
            >
              <i class="fas fa-edit mr-2"></i>
              發表貼文
            </button>
          </div>
        </template>
      </div>
    </div>

    <!-- 錯誤狀態 -->
    <div v-else class="text-center py-12">
      <div class="w-24 h-24 mx-auto mb-4 rounded-full yin-yang opacity-50"></div>
      <h3 class="text-xl font-medium text-wood-700 mb-2">武場不存在</h3>
      <p class="text-wood-500 mb-4">找不到指定的武場，可能已被刪除或您沒有權限檢視。</p>
      <button
        @click="goBack"
        class="px-6 py-3 bg-wood-500 hover:bg-wood-600 text-white rounded-xl transition-all duration-300"
      >
        <i class="fas fa-arrow-left mr-2"></i>
        回到武場列表
      </button>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed, watch } from 'vue'
import { useUserStore } from '../stores/user.js'
import { useClubsStore } from '../stores/clubs.js'
import { usePostsStore } from '../stores/posts.js'
import Post from './Post.vue'

// Props
const props = defineProps({
  clubId: {
    type: [String, Number],
    required: true
  }
})

// Emits
const emit = defineEmits(['openPostForm', 'showPostDetail'])

// 使用 Pinia stores
const userStore = useUserStore()
const clubsStore = useClubsStore()
const postsStore = usePostsStore()

// 組件狀態
const isLoading = ref(true)
const club = ref(null)
const members = ref([])
const posts = ref([])
const userClubs = ref([])
const isLoadingPosts = ref(false)
const hasMorePosts = ref(true)
const isMemberValue = ref(false)
const isAdminValue = ref(false)

// 計算屬性
const isMember = computed(() => {
  return isMemberValue.value
})

const isAdmin = computed(() => {
  return isAdminValue.value
})

// 方法
const formatDate = (dateString) => {
  return new Date(dateString).toLocaleString('zh-TW', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  })
}

const getMemberStatusClass = (permission) => {
  switch (permission) {
    case '等候加入':
      return 'bg-yellow-100 text-yellow-800'
    case '一般成員':
      return 'bg-green-100 text-green-800'
    case '助理管理員':
      return 'bg-blue-100 text-blue-800'
    case '最高管理員':
      return 'bg-purple-100 text-purple-800'
    default:
      return 'bg-gray-100 text-gray-800'
  }
}

// 事件處理
const joinClub = async () => {
  try {
    await clubsStore.joinClub(club.value.id)
  } catch (error) {
    console.error('加入武場失敗:', error)
  }
}

const leaveClub = async () => {
  if (confirm(`您確定要退出「${club.value.name}」武場嗎？您一旦退出，可能需要重新申請加入。`)) {
    try {
      await clubsStore.leaveClub(club.value.id)
    } catch (error) {
      console.error('退出武場失敗:', error)
    }
  }
}

const editClub = () => {
  const url = `/clubs/${club.value.id}/edit`
  window.history.pushState(null, '', url)
  
  // 觸發路由變更事件
  const event = new PopStateEvent('popstate')
  window.dispatchEvent(event)
}

const approveMember = async (member) => {
  try {
    // 這裡需要實作同意成員的 API 調用
    console.log('同意成員:', member)
    // 重新載入成員列表
    await loadMembers()
  } catch (error) {
    console.error('同意成員失敗:', error)
  }
}

const kickMember = async (member) => {
  if (confirm(`您確定要將「${member.user?.nickname}」踢出武場嗎？`)) {
    try {
      // 這裡需要實作踢出成員的 API 調用
      console.log('踢出成員:', member)
      // 重新載入成員列表
      await loadMembers()
    } catch (error) {
      console.error('踢出成員失敗:', error)
    }
  }
}

const goBack = () => {
  // 導航回武場列表
  window.history.pushState(null, '', '/clubs')
  window.dispatchEvent(new Event('popstate'))
}

// 貼文相關事件處理
const handleLike = async (postId) => {
  await postsStore.likePost(postId)
  // 更新本地貼文狀態
  const post = posts.value.find(p => p.id === postId)
  if (post) {
    post.isLiked = !post.isLiked
    post.likes += post.isLiked ? 1 : -1
  }
}

const handleBookmark = async (postId) => {
  await postsStore.bookMark(postId)
  // 更新本地貼文狀態
  const post = posts.value.find(p => p.id === postId)
  if (post) {
    post.isBookmarked = !post.isBookmarked
  }
}

const handleShowMore = (postId) => {
  postsStore.showMore(postId)
  // 更新本地貼文狀態
  const post = posts.value.find(p => p.id === postId)
  if (post) {
    post.showMore = true
  }
}

const showPostDetail = (postId) => {
  emit('showPostDetail', postId)
}

const openPostForm = (post = null) => {
  emit('openPostForm', post)
}

// 資料載入
const loadClubData = async () => {
  try {
    const response = await fetch(`/clubs/${props.clubId}.json`)
    if (response.ok) {
      const data = await response.json()
      club.value = data.club
      members.value = data.members || []
      posts.value = data.posts || []
      
      // 設定成員和管理員狀態
      isMemberValue.value = data.is_member
      isAdminValue.value = data.is_admin
      
      return true
    } else {
      club.value = null
      return false
    }
  } catch (error) {
    console.error('載入武場資料失敗:', error)
    club.value = null
    return false
  }
}

const loadPosts = async (offset = 0) => {
  try {
    isLoadingPosts.value = true
    const url = `/clubs/${props.clubId}/posts.json${offset ? `?from=${offset}` : ''}`
    const response = await fetch(url)
    
    if (response.ok) {
      const newPosts = await response.json()
      if (offset === 0) {
        posts.value = newPosts
      } else {
        posts.value.push(...newPosts)
      }
      hasMorePosts.value = newPosts.length === 15 // 假設每次載入15筆
    }
  } catch (error) {
    console.error('載入武場貼文失敗:', error)
    if (offset === 0) {
      posts.value = []
    }
  } finally {
    isLoadingPosts.value = false
  }
}

const loadMorePosts = async () => {
  if (posts.value.length > 0) {
    const lastPost = posts.value[posts.value.length - 1]
    await loadPosts(lastPost.id)
  }
}

const loadUserClubs = async () => {
  if (userStore.isLoggedIn) {
    try {
      userClubs.value = await userStore.getUserClubs()
    } catch (error) {
      console.error('載入使用者武場失敗:', error)
      userClubs.value = []
    }
  }
}

// 載入所有資料的通用方法
const loadAllData = async () => {
  try {
    isLoading.value = true
    await Promise.all([
      loadClubData(),
      loadUserClubs()
    ])
  } finally {
    isLoading.value = false
  }
}

// 組件掛載時載入資料
onMounted(loadAllData)

// 監聽 clubId 變化，重新載入資料
watch(() => props.clubId, async (newClubId, oldClubId) => {
  if (newClubId && newClubId !== oldClubId) {
    console.log('ClubDetail: clubId 變化，重新載入資料', { newClubId, oldClubId })
    await loadAllData()
  }
}, { immediate: false })
</script>

<style scoped>
/* 使用與其他組件相同的樣式系統 */
.club-detail {
  @apply min-h-screen;
}

/* 繼承樣式定義 */
.yin-yang {
  background: conic-gradient(from 0deg, #000 0deg 180deg, #fff 180deg 360deg);
  position: relative;
}

.yin-yang::before {
  content: '';
  position: absolute;
  top: 25%;
  left: 50%;
  width: 50%;
  height: 50%;
  background: #fff;
  border-radius: 50%;
  transform: translateX(-50%);
}

.yin-yang::after {
  content: '';
  position: absolute;
  top: 75%;
  left: 50%;
  width: 50%;
  height: 50%;
  background: #000;
  border-radius: 50%;
  transform: translateX(-50%);
}

.post-shadow {
  box-shadow: 
    0 4px 6px -1px rgba(0, 0, 0, 0.1),
    0 2px 4px -1px rgba(0, 0, 0, 0.06),
    0 0 0 1px rgba(196, 166, 116, 0.1);
}

@keyframes pulseSoft {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.7; }
}

.animate-pulse-soft {
  animation: pulseSoft 2s ease-in-out infinite;
}

/* 木質色彩系統 */
.bg-wood-50 { background-color: #faf8f3; }
.bg-wood-100 { background-color: #f4f0e6; }
.bg-wood-200 { background-color: #e8dcc0; }
.bg-wood-300 { background-color: #dcc89a; }
.bg-wood-500 { background-color: #a68b5b; }
.bg-wood-600 { background-color: #8b7355; }
.bg-wood-700 { background-color: #6d5a47; }
.bg-wood-800 { background-color: #4a3f35; }

.text-wood-500 { color: #a68b5b; }
.text-wood-600 { color: #8b7355; }
.text-wood-700 { color: #6d5a47; }
.text-wood-800 { color: #4a3f35; }

.hover\:bg-wood-300:hover { background-color: #dcc89a; }
.hover\:bg-wood-600:hover { background-color: #8b7355; }

/* 漸變背景效果 */
.bg-gradient-to-r {
  background-image: linear-gradient(to right, var(--tw-gradient-stops));
}

.from-wood-500 {
  --tw-gradient-from: #a68b5b;
  --tw-gradient-stops: var(--tw-gradient-from), var(--tw-gradient-to, rgba(166, 139, 91, 0));
}

.to-wood-600 {
  --tw-gradient-to: #8b7355;
}

.from-wood-600 {
  --tw-gradient-from: #8b7355;
  --tw-gradient-stops: var(--tw-gradient-from), var(--tw-gradient-to, rgba(139, 115, 85, 0));
}

.to-wood-700 {
  --tw-gradient-to: #6d5a47;
}

.from-blue-500 {
  --tw-gradient-from: #3b82f6;
  --tw-gradient-stops: var(--tw-gradient-from), var(--tw-gradient-to, rgba(59, 130, 246, 0));
}

.to-blue-600 {
  --tw-gradient-to: #2563eb;
}

.from-blue-600 {
  --tw-gradient-from: #2563eb;
  --tw-gradient-stops: var(--tw-gradient-from), var(--tw-gradient-to, rgba(37, 99, 235, 0));
}

.to-blue-700 {
  --tw-gradient-to: #1d4ed8;
}

.from-red-500 {
  --tw-gradient-from: #ef4444;
  --tw-gradient-stops: var(--tw-gradient-from), var(--tw-gradient-to, rgba(239, 68, 68, 0));
}

.to-red-600 {
  --tw-gradient-to: #dc2626;
}

.from-red-600 {
  --tw-gradient-from: #dc2626;
  --tw-gradient-stops: var(--tw-gradient-from), var(--tw-gradient-to, rgba(220, 38, 38, 0));
}

.to-red-700 {
  --tw-gradient-to: #b91c1c;
}
</style>